/**
 * Google Integration API Service
 * Per-entity Google OAuth connect/status/disconnect — lets a bot send Gmail as
 * the connected user instead of the platform's fixed account.
 */

import AuthUtils from "@/providers/firebase/authUtils";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8000";

export interface ApiResponse<T = Record<string, unknown>> {
  status: boolean;
  statusCode: number;
  message: string;
  data: T | null;
}

async function apiCall<T>(
  path: string,
  options: RequestInit = {},
): Promise<ApiResponse<T>> {
  const headers = await AuthUtils.getAuthHeaders();
  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: { ...headers, ...options.headers },
  });
  return res.json();
}

export interface GoogleConnectUrlResponse {
  authorization_url: string;
  capability?: GoogleCapability;
}

/**
 * Google is TWO labelled cards on ONE credential (D-133-016). A connect names the capability it
 * is connecting, so connecting the calendar never asks for mail read — and each card renders
 * its own state from the grants the credential actually holds.
 */
export type GoogleCapability = "mail" | "calendar";

export interface GoogleIntegrationStatus {
  connected: boolean;
  email: string | null;
  connectedAt: string | null;
  /**
   * Credential status recorded by the BE (canonical `IntegrationVault.status` vocabulary:
   * active | expired | error). Null = not reported. PROJECT-066 T19.
   * One concept, one name (RULES_NAMING §4) — the FE derives any flag it needs from this,
   * rather than asking the BE for a second representation that could disagree.
   */
  status: string | null;
  /** Access-token expiry (ISO). The refresh token itself carries no expiry. */
  expiresAt: string | null;
  /** The granted scopes Google returned at connect time — the source of per-card state. */
  scopes?: string[];
  /** Which capabilities those grants can actually serve: "mail" | "calendar". */
  capabilities?: string[];
}

export interface GoogleCredentialStatus {
  /**
   * active | expired | error | not_connected (no vault entry) | unchecked (the check itself
   * did not complete — NOT a verdict on the credential).
   */
  status: string;
  expiresAt: string | null;
}

export async function getGoogleConnectUrl(
  entityId: string,
  capability: GoogleCapability = "mail",
): Promise<ApiResponse<GoogleConnectUrlResponse>> {
  return apiCall("/api/v1/integrations/google/connect", {
    method: "POST",
    body: JSON.stringify({ entity_id: entityId, capability }),
  });
}

export async function getGoogleIntegrationStatus(
  entityId: string,
): Promise<ApiResponse<GoogleIntegrationStatus>> {
  return apiCall(`/api/v1/integrations/google/status?entity_id=${encodeURIComponent(entityId)}`);
}

export async function disconnectGoogleIntegration(
  entityId: string,
): Promise<ApiResponse<{ disconnected: boolean }>> {
  return apiCall("/api/v1/integrations/google/disconnect", {
    method: "POST",
    body: JSON.stringify({ entity_id: entityId }),
  });
}

/**
 * PROJECT-066 T18/T19 — verify the stored Google credential. Refreshes the token
 * server-side (the FE never sees a token) and records the verdict, so "connected"
 * stops hiding a dead credential. Returns a STATUS, never an error.
 */
export async function verifyGoogleCredential(
  entityId: string,
): Promise<ApiResponse<GoogleCredentialStatus>> {
  return apiCall("/api/v1/integrations/google/verify_credential", {
    method: "POST",
    body: JSON.stringify({ entity_id: entityId }),
  });
}
